/**
 * PortalRemessaPage — self-service de remessa do parceiro (E21.4.3b, FR-17 portal).
 *
 * O parceiro escolhe um Template + um Terminal SEU + data + nº do chamado, clica
 * em Gerar e o sistema auto-preenche (razão social/CNPJ/cidade/contato + pinpad/
 * impressora) + aloca o protocolo + gera o PDF (WeasyPrint, backend) e baixa.
 * Escopo restrito ao próprio parceiro (terminais/histórico) — gate no backend.
 */
import { useEffect, useState } from "react";
import { Download, FileOutput, Loader2, Search } from "lucide-react";

import {
  usePortalGenerateRemessa,
  usePortalRemessaDownload,
  usePortalRemessaGenerations,
  usePortalRemessaTemplates,
  usePortalRemessaTerminals,
} from "@/hooks/usePortalRemessa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(iso);
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PortalRemessaPage() {
  const templates = usePortalRemessaTemplates().data ?? [];

  const [templateId, setTemplateId] = useState("");
  const [terminalId, setTerminalId] = useState("");
  const [dataRemessa, setDataRemessa] = useState("");
  const [chamado, setChamado] = useState("");
  const [tecnico, setTecnico] = useState("");

  const [termSearch, setTermSearch] = useState("");
  const [termQuery, setTermQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTermQuery(termSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [termSearch]);
  const terminalsQuery = usePortalRemessaTerminals(termQuery);
  const terminals = terminalsQuery.data ?? [];

  const generate = usePortalGenerateRemessa();
  const redownload = usePortalRemessaDownload();
  const history = usePortalRemessaGenerations(1, 20);

  const canGenerate =
    !!templateId &&
    !!terminalId &&
    !!dataRemessa &&
    !!chamado.trim() &&
    !generate.isPending;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    await generate.mutateAsync({
      templateId,
      payload: {
        terminal_id: terminalId,
        data_remessa: dataRemessa,
        chamado_number: chamado.trim(),
        tecnico: tecnico.trim() || null,
      },
    });
    setTerminalId("");
    setChamado("");
    setTecnico("");
  };

  const items = history.data?.items ?? [];
  const noTemplates = templates.length === 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center gap-2">
        <FileOutput className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gerar Remessa</h1>
          <p className="text-muted-foreground">
            Etiqueta/protocolo do seu terminal, preenchido automaticamente (PDF).
          </p>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Nova remessa</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {noTemplates ? (
            <p className="text-sm text-muted-foreground">
              Nenhum modelo de remessa disponível no momento.
            </p>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Modelo</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger data-testid="portal-remessa-template">
                      <SelectValue placeholder="Selecione o modelo" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Terminal</Label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={termSearch}
                      onChange={(e) => setTermSearch(e.target.value)}
                      placeholder="Buscar terminal/loja…"
                      className="mb-1.5 pl-8"
                      data-testid="portal-remessa-terminal-search"
                    />
                  </div>
                  <Select value={terminalId} onValueChange={setTerminalId}>
                    <SelectTrigger data-testid="portal-remessa-terminal">
                      <SelectValue
                        placeholder={
                          terminalsQuery.isFetching ? "Buscando…" : "Selecione o terminal"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {terminals.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          Nenhum terminal encontrado.
                        </div>
                      ) : (
                        terminals.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Data da remessa</Label>
                  <Input
                    type="date"
                    value={dataRemessa}
                    onChange={(e) => setDataRemessa(e.target.value)}
                    data-testid="portal-remessa-data"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Nº do chamado</Label>
                  <Input
                    value={chamado}
                    onChange={(e) => setChamado(e.target.value)}
                    placeholder="Ex.: CH-12345"
                    data-testid="portal-remessa-chamado"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Técnico (opcional)</Label>
                  <Input
                    value={tecnico}
                    onChange={(e) => setTecnico(e.target.value)}
                    placeholder="Nome do técnico"
                    data-testid="portal-remessa-tecnico"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  data-testid="portal-remessa-generate"
                >
                  {generate.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Gerar e baixar PDF
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Minhas remessas</h2>
        {history.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        ) : items.length === 0 ? (
          <p
            className="py-6 text-sm text-muted-foreground"
            data-testid="portal-remessa-history-empty"
          >
            Nenhuma remessa gerada ainda.
          </p>
        ) : (
          <div className="rounded-md border" data-testid="portal-remessa-history">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Protocolo</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Chamado</TableHead>
                  <TableHead className="text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((g) => (
                  <TableRow key={g.id} data-testid="portal-remessa-history-row">
                    <TableCell className="font-mono">{g.protocol_number}</TableCell>
                    <TableCell>{g.template_name ?? "—"}</TableCell>
                    <TableCell>{formatDate(g.data_remessa)}</TableCell>
                    <TableCell>{g.chamado_number}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Baixar PDF"
                        title="Baixar PDF"
                        disabled={redownload.isPending && redownload.variables === g.id}
                        onClick={() => redownload.mutate(g.id)}
                        data-testid="portal-remessa-redownload"
                      >
                        {redownload.isPending && redownload.variables === g.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
